// services/auth/DirectCognitoApolloService.js
// Apollo Client with Direct Cognito ID Token Authentication
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  from,
  Observable,
  fromPromise
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { logger } from './Logger';
import { createObjectIdTransformLink } from '../apollo/ObjectIdTransformLink';

export class DirectCognitoApolloService {
  constructor(options = {}) {
    this.graphqlEndpoint = options.graphqlEndpoint;
    this.cognitoService = options.cognitoService;
    this.tokenService = options.tokenService;
    this.onAuthError = options.onAuthError || (() => {});
    this.pendingRequests = [];
    this.isRefreshing = false;
    this.latestIdToken = null;
    this.tokenMetrics = {};

    logger.debug('DirectCognitoApolloService initialized');

    // Event listener for token updates
    if (options.authService) {
      this.authService = options.authService;
      this.authService.addEventListener(
        'apollo:tokenRefreshed',
        this._handleTokenRefreshed.bind(this)
      );
      logger.debug('Registered for apollo:tokenRefreshed events');
    }
  }

  // Validate Cognito ID token format
  _validateToken(token, source = 'unknown') {
    if (!token || typeof token !== 'string') {
      logger.error(
        `Invalid token from ${source}: Token is null or not a string`
      );
      return false;
    }

    const isValid = this.tokenService.isValidJwtFormat(token);

    if (isValid) {
      const tokenType = this.tokenService.getTokenType(token);
      if (tokenType !== 'cognito_id_token') {
        logger.warn(
          `Token from ${source} is type '${tokenType}', expected 'cognito_id_token'`
        );
        // Still allow it as long as it's a valid JWT
      }
      logger.debug(`Token from ${source} validated: type=${tokenType}`);
    } else {
      logger.error(`Token from ${source} has invalid JWT format`);
    }

    return isValid;
  }

  // Monitor token metrics for analysis
  _monitorToken(token, source) {
    if (!token) {
      logger.warn(`Null token from source: ${source}`);
      return;
    }

    const segments = token.split('.');
    logger.debug(
      `Token from ${source}: ${
        segments.length
      } segments, first 15 chars: ${token.substring(0, 15)}...`
    );

    this.tokenMetrics[source] = {
      timestamp: Date.now(),
      segments: segments.length,
      length: token.length,
      type: this.tokenService.getTokenType(token)
    };
  }

  // Get fresh Cognito ID token for GraphQL requests
  async getAuthToken() {
    logger.debug('Getting fresh Cognito ID token for direct GraphQL request');

    // Try to use the latest cached ID token
    if (
      this.latestIdToken &&
      this._validateToken(this.latestIdToken, 'latest_id_token_cache')
    ) {
      this._monitorToken(this.latestIdToken, 'latest_id_token_cache');
      logger.debug('Using cached ID token for direct GraphQL request');
      console.log(
        'USING CACHED COGNITO ID TOKEN FROM MEMORY:',
        this.latestIdToken
      );
      return this.latestIdToken;
    }

    // Try to get token from TokenService cache
    if (this.tokenService) {
      const cachedToken = this.tokenService.getToken('idToken');
      if (
        cachedToken &&
        this._validateToken(cachedToken, 'token_service_id_cache')
      ) {
        this._monitorToken(cachedToken, 'token_service_id_cache');
        this.latestIdToken = cachedToken;
        logger.debug('Using cached ID token from TokenService');
        console.log(
          'USING CACHED COGNITO ID TOKEN FROM TOKEN SERVICE:',
          cachedToken
        );
        return cachedToken;
      }
    }

    // No valid ID token in cache, refresh tokens
    try {
      logger.debug('No valid ID token in cache, refreshing Cognito session');
      
      // Use CognitoService to refresh the session
      const session = await this.cognitoService.refreshIdToken();
      
      if (session && session.idToken) {
        const freshToken = session.idToken.jwtToken;
        if (this._validateToken(freshToken, 'refreshed_id_token')) {
          this._monitorToken(freshToken, 'refreshed_id_token');
          this.latestIdToken = freshToken;
          logger.debug('Using refreshed Cognito ID token');
          console.log('USING REFRESHED COGNITO ID TOKEN:', freshToken);
          return freshToken;
        }
      }

      // If refresh fails, try to get current session
      logger.debug('Token refresh failed, trying to get current session');
      const currentSession = await this.cognitoService.getSession();
      
      if (currentSession && currentSession.idToken) {
        const currentToken = currentSession.idToken.jwtToken;
        if (this._validateToken(currentToken, 'current_session_token')) {
          this._monitorToken(currentToken, 'current_session_token');
          this.latestIdToken = currentToken;
          logger.debug('Using current session ID token');
          console.log('USING CURRENT SESSION COGNITO ID TOKEN:', currentToken);
          return currentToken;
        }
      }

      // If everything fails, throw error
      logger.error('No valid Cognito ID token available after refresh attempts');
      throw new Error('No valid Cognito ID token available');
    } catch (error) {
      logger.error('Error getting Cognito ID token for direct GraphQL request', error);
      this.onAuthError();
      throw error;
    }
  }

  // Recovery from token errors
  async _recoverFromTokenError() {
    logger.debug('Attempting to recover from token error');

    // Clear all token caches
    if (this.tokenService) {
      this.tokenService.clearAllTokens();
    }
    this.latestIdToken = null;

    try {
      // Try to refresh Cognito session
      if (this.cognitoService) {
        const session = await this.cognitoService.refreshIdToken();
        if (session && session.idToken) {
          logger.debug('Successfully recovered by refreshing Cognito session');
          return true;
        }
      }

      // If that fails, try to get current session
      const currentSession = await this.cognitoService.getSession();
      if (currentSession && currentSession.idToken) {
        logger.debug('Recovered by getting current Cognito session');
        return true;
      }

      logger.error('Failed to recover - no valid Cognito session available');
      return false;
    } catch (error) {
      logger.error('Failed to recover from token error', error);
      return false;
    }
  }

  // Handle token refresh events
  _handleTokenRefreshed(data) {
    logger.debug('Received token refresh event with new tokens');

    // Update ID token if provided
    if (
      data.idToken &&
      this._validateToken(data.idToken, 'id_token_refresh_event')
    ) {
      this._monitorToken(data.idToken, 'id_token_refresh_event');
      this.latestIdToken = data.idToken;

      if (this.tokenService) {
        this.tokenService.setToken('idToken', data.idToken);
        logger.debug(
          `Updated ID token in TokenService: ${data.idToken.substring(0, 15)}...`
        );
        console.log(
          'RECEIVED REFRESHED COGNITO ID TOKEN IN DIRECT APOLLO SERVICE:',
          data.idToken
        );
      }

      logger.debug(
        `Updated latest ID token in DirectCognitoApolloService: ${data.idToken.substring(
          0,
          15
        )}...`
      );
    } else if (data.idToken) {
      logger.error('Received invalid ID token in refresh event, ignoring');
    }
  }

  // Create Apollo Client with direct Cognito authentication
  createApolloClient() {
    logger.debug('Creating Apollo client with direct Cognito authentication');

    // HTTP Link for GraphQL requests
    const httpLink = new HttpLink({
      uri: this.graphqlEndpoint,
      fetch: async (uri, options) => {
        try {
          // Get fresh Cognito ID token
          const idToken = await this.getAuthToken();

          console.log(
            'ACTUAL COGNITO ID TOKEN USED FOR DIRECT APOLLO CLIENT REQUEST:',
            idToken
          );

          logger.debug(
            `Direct Apollo fetch handler using Cognito ID token: ${idToken.substring(
              0,
              15
            )}...`
          );

          // Set Authorization header with Cognito ID token
          if (options.headers.Authorization) {
            logger.debug(
              `Removing existing Authorization header: ${options.headers.Authorization}`
            );
          }
          delete options.headers.Authorization;
          options.headers.Authorization = `Bearer ${idToken}`;
          logger.debug(
            `Set Authorization header with Cognito ID token: ${options.headers.Authorization.substring(
              0,
              20
            )}...`
          );

          return fetch(uri, options);
        } catch (error) {
          logger.error('Error in direct Apollo fetch handler', error);

          // Try recovery for auth-related errors
          if (
            error.message &&
            (error.message.includes('token') ||
              error.message.includes('auth') ||
              error.message.includes('unauthorized'))
          ) {
            logger.debug('Auth-related error detected, attempting recovery');
            try {
              await this._recoverFromTokenError();
              const recoveredToken = await this.getAuthToken();
              
              if (options.headers.Authorization) {
                logger.debug(
                  `Removing existing Authorization header after recovery: ${options.headers.Authorization}`
                );
              }
              delete options.headers.Authorization;
              options.headers.Authorization = `Bearer ${recoveredToken}`;
              logger.debug(
                `Set Authorization header after recovery: ${options.headers.Authorization.substring(
                  0,
                  20
                )}...`
              );

              return fetch(uri, options);
            } catch (recoveryError) {
              logger.error(
                'Recovery failed, proceeding with original error',
                recoveryError
              );
            }
          }

          throw error;
        }
      }
    });

    // Error Link for handling authentication errors
    const errorLink = onError(
      ({ graphQLErrors, networkError, operation, forward }) => {
        // Handle GraphQL authentication errors
        if (graphQLErrors) {
          const authError = graphQLErrors.find(
            (error) =>
              error.extensions?.code === 'UNAUTHENTICATED' ||
              error.message.includes('auth') ||
              error.message.includes('token') ||
              error.message.includes('unauthorized')
          );

          if (authError) {
            logger.debug('GraphQL auth error detected:', authError.message);
            return this._handleTokenRefresh(operation, forward);
          }
        }

        // Handle network authentication errors
        if (
          networkError &&
          'statusCode' in networkError &&
          (networkError.statusCode === 401 || networkError.statusCode === 403)
        ) {
          logger.debug(`Network ${networkError.statusCode} error detected`);
          return this._handleTokenRefresh(operation, forward);
        }
      }
    );

    // ObjectId Transform Link for converting ObjectId scalars to MongoID
    const objectIdTransformLink = createObjectIdTransformLink();

    // Request Link for pre-flight token checking
    const requestLink = new ApolloLink((operation, forward) => {
      // Check if ID token needs refresh
      if (
        this.tokenService &&
        this.tokenService.shouldRefreshToken('idToken')
      ) {
        logger.debug('ID token refresh needed before request');

        if (this.isRefreshing) {
          logger.debug('Token refresh already in progress, queueing request');
          return new Observable((observer) => {
            this.pendingRequests.push(() => {
              const subscription = forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer)
              });
              return () => subscription.unsubscribe();
            });
          });
        }

        this.isRefreshing = true;

        return fromPromise(
          this.cognitoService.refreshIdToken().finally(() => {
            this.isRefreshing = false;
            this._resolvePendingRequests();
          })
        ).flatMap(() => forward(operation));
      }

      return forward(operation);
    });

    // Create Apollo Client
    return new ApolloClient({
      link: from([objectIdTransformLink, requestLink, httpLink, errorLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          errorPolicy: 'all'
        },
        query: {
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        },
        mutate: {
          errorPolicy: 'all'
        }
      },
      assumeImmutableResults: true,
      queryDeduplication: true,
      connectToDevTools: true
    });
  }

  // Handle token refresh for authentication errors
  _handleTokenRefresh(operation, forward) {
    if (this.isRefreshing) {
      logger.debug('Token refresh already in progress, queueing request');
      return new Observable((observer) => {
        this.pendingRequests.push(() => {
          const subscription = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer)
          });
          return () => subscription.unsubscribe();
        });
      });
    }

    this.isRefreshing = true;

    return fromPromise(
      this.cognitoService
        .refreshIdToken()
        .then(async (session) => {
          logger.debug(`Cognito token refresh completed, success: ${!!session}`);

          if (!session || !session.idToken) {
            logger.debug('Token refresh failed, attempting recovery');
            const recoverySuccess = await this._recoverFromTokenError();

            if (!recoverySuccess) {
              this.onAuthError();
              throw new Error('Authentication failed');
            }
          }

          const idToken = await this.getAuthToken();
          console.log('ACTUAL COGNITO ID TOKEN USED AFTER TOKEN REFRESH:', idToken);

          if (idToken) {
            if (!this._validateToken(idToken, 'token_refresh_handler')) {
              logger.error('Invalid ID token after refresh, attempting recovery');
              await this._recoverFromTokenError();
              const recoveredToken = await this.getAuthToken();
              console.log('ACTUAL COGNITO ID TOKEN USED AFTER RECOVERY:', recoveredToken);

              if (!this._validateToken(recoveredToken, 'token_refresh_recovery')) {
                this.onAuthError();
                throw new Error('Unable to obtain valid ID token after recovery');
              }

              this._monitorToken(recoveredToken, 'token_refresh_recovery');
              operation.setContext(({ headers = {} }) => {
                const newHeaders = { ...headers };
                if (newHeaders.Authorization) {
                  logger.debug(
                    `Removing existing Authorization header in setContext (recovery): ${newHeaders.Authorization}`
                  );
                }
                delete newHeaders.Authorization;

                return {
                  headers: {
                    ...newHeaders,
                    Authorization: `Bearer ${recoveredToken}`
                  }
                };
              });
            } else {
              this._monitorToken(idToken, 'token_refresh_handler');
              operation.setContext(({ headers = {} }) => {
                const newHeaders = { ...headers };
                if (newHeaders.Authorization) {
                  logger.debug(
                    `Removing existing Authorization header in setContext: ${newHeaders.Authorization}`
                  );
                }
                delete newHeaders.Authorization;

                return {
                  headers: {
                    ...newHeaders,
                    Authorization: `Bearer ${idToken}`
                  }
                };
              });
            }
          } else {
            logger.error('No ID token available after token refresh');
            this.onAuthError();
            throw new Error('No ID token available');
          }

          return true;
        })
        .finally(() => {
          this.isRefreshing = false;
          this._resolvePendingRequests();
        })
    ).flatMap(() => forward(operation));
  }

  // Resolve pending requests
  _resolvePendingRequests() {
    logger.debug(`Resolving ${this.pendingRequests.length} pending requests`);

    const pendingRequestsCopy = [...this.pendingRequests];
    this.pendingRequests = [];

    pendingRequestsCopy.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        logger.error('Error executing pending request callback', error);
      }
    });
  }
}